<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  templateName: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const getDelayLabel = seconds => {
  if (!seconds || seconds === 0) return '';
  if (seconds < 60)
    return t('FOLLOW_UP_BUILDER.PREVIEW.WAIT_SECONDS', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  return t('FOLLOW_UP_BUILDER.PREVIEW.WAIT_MINUTES', { count: minutes });
};

const formatFileSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const currentTime = computed(() => {
  const now = new Date();
  return now.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
});
</script>

<template>
  <div
    class="flex flex-col h-full rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
  >
    <!-- WhatsApp Header -->
    <div class="flex-shrink-0 bg-[#075e54] dark:bg-[#1f2c34] px-4 py-3">
      <div class="flex items-center gap-3">
        <div
          class="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"
        >
          <span class="i-lucide-user size-5 text-white/80" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-white truncate">
            {{ templateName || t('FOLLOW_UP_BUILDER.PREVIEW.CONTACT_NAME') }}
          </p>
          <p class="text-xs text-white/60">
            {{ t('FOLLOW_UP_BUILDER.PREVIEW.ONLINE') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Chat Body -->
    <div
      class="flex-1 overflow-y-auto p-4 space-y-2 bg-[#e5ddd5] dark:bg-slate-800"
    >
      <!-- Empty state -->
      <div
        v-if="items.length === 0"
        class="flex flex-col items-center justify-center h-full text-center"
      >
        <span
          class="i-lucide-message-square-dashed size-12 text-slate-400/60 mb-3"
        />
        <p class="text-sm text-slate-500/80">
          {{ t('FOLLOW_UP_BUILDER.PREVIEW.EMPTY') }}
        </p>
      </div>

      <!-- Messages -->
      <template v-for="(item, index) in items" :key="item.id || index">
        <!-- Delay indicator -->
        <div v-if="item.delay_seconds > 0" class="flex justify-center my-2">
          <div
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/80 dark:bg-slate-700/80 shadow-sm"
          >
            <span class="i-lucide-clock size-3 text-slate-500" />
            <span
              class="text-xs text-slate-600 dark:text-slate-300 font-medium"
            >
              {{ getDelayLabel(item.delay_seconds) }}
            </span>
          </div>
        </div>

        <!-- Message bubble -->
        <div class="flex justify-end">
          <div
            class="max-w-[85%] rounded-lg px-3 py-2 shadow-sm relative bg-[#dcf8c6] dark:bg-[#005c4b]"
          >
            <!-- Text message -->
            <template v-if="item.item_type === 'text'">
              <p
                class="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words"
              >
                {{ item.content || t('FOLLOW_UP_BUILDER.PREVIEW.EMPTY_TEXT') }}
              </p>
            </template>

            <!-- Image message -->
            <template v-else-if="item.item_type === 'image'">
              <div
                class="w-48 h-32 rounded bg-slate-200 dark:bg-slate-600 flex items-center justify-center mb-1"
              >
                <img
                  v-if="item.attachment?.url"
                  :src="item.attachment.url"
                  class="w-full h-full object-cover rounded"
                  :alt="item.attachment?.filename || 'image'"
                />
                <span v-else class="i-lucide-image size-8 text-slate-400" />
              </div>
              <p
                v-if="item.content"
                class="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap"
              >
                {{ item.content }}
              </p>
            </template>

            <!-- Audio message -->
            <template v-else-if="item.item_type === 'audio'">
              <div class="flex items-center gap-2 min-w-[180px]">
                <div
                  class="w-8 h-8 rounded-full bg-[#075e54] flex items-center justify-center flex-shrink-0"
                >
                  <span class="i-lucide-play size-4 text-white" />
                </div>
                <div class="flex-1">
                  <div
                    class="h-1.5 bg-slate-300/60 dark:bg-slate-500/60 rounded-full"
                  >
                    <div class="h-1.5 bg-[#075e54] rounded-full w-0" />
                  </div>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {{
                      item.attachment?.filename ||
                      t('FOLLOW_UP_BUILDER.PREVIEW.AUDIO')
                    }}
                  </p>
                </div>
              </div>
            </template>

            <!-- Video message -->
            <template v-else-if="item.item_type === 'video'">
              <div
                class="w-48 h-32 rounded bg-slate-700 flex items-center justify-center mb-1 relative"
              >
                <div
                  class="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center"
                >
                  <span class="i-lucide-play size-6 text-white" />
                </div>
              </div>
              <p
                v-if="item.content"
                class="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap"
              >
                {{ item.content }}
              </p>
            </template>

            <!-- Document message -->
            <template v-else-if="item.item_type === 'document'">
              <div
                class="flex items-center gap-2 p-2 bg-white/50 dark:bg-slate-600/50 rounded mb-1 min-w-[180px]"
              >
                <span
                  class="i-lucide-file-text size-8 text-red-500 flex-shrink-0"
                />
                <div class="flex-1 min-w-0">
                  <p
                    class="text-sm font-medium text-slate-700 dark:text-slate-200 truncate"
                  >
                    {{
                      item.attachment?.filename ||
                      t('FOLLOW_UP_BUILDER.PREVIEW.DOCUMENT')
                    }}
                  </p>
                  <p
                    v-if="item.attachment?.byte_size"
                    class="text-xs text-slate-500 dark:text-slate-400"
                  >
                    {{ formatFileSize(item.attachment.byte_size) }}
                  </p>
                </div>
              </div>
            </template>

            <!-- Timestamp -->
            <div class="flex justify-end mt-0.5">
              <span class="text-[10px] text-slate-500 dark:text-slate-400">
                {{ currentTime }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
